/**
 * useUpdateChecker Hook
 * 
 * Why this matters: This hook is the core of our update notification system.
 * It periodically checks GitHub for new commits, manages notification state,
 * and provides methods to dismiss updates. It runs in the background while
 * users are using the app to detect new deployments.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchLatestCommits, filterMeaningfulCommits, getCommitSummary, getConfiguredRepos } from '../services/githubApi';

// localStorage key prefix; the repo key ('frontend' | 'backend') is appended.
// Why this matters: Each watched repo needs its own "last seen commit" record,
// otherwise marking one repo's commits as seen would also silence the other's.
const LAST_KNOWN_COMMIT_PREFIX = 'us-citrus-ai-last-known-commit';

// Legacy single-repo key used before multi-repo support.
// Why this matters: Existing users have a value under this old key. Reusing it
// as the frontend repo's seed avoids a spurious "update available" burst on the
// first load after this change ships.
const LEGACY_LAST_KNOWN_COMMIT_KEY = 'us-citrus-ai-last-known-commit';

interface GitHubCommit {
  sha: string;
  commit: {
    message: string;
    author: { 
      date: string;
      name: string;
    };
  };
  html_url: string;
}

interface UpdateCheckerState {
  hasUpdate: boolean;
  latestCommits: GitHubCommit[];
  isChecking: boolean;
  lastChecked: Date | null;
  error: string | null;
  summary: string;
}

interface UpdateCheckerOptions {
  checkInterval?: number; // in milliseconds, default 5 minutes
  enabled?: boolean; // allow users to disable checking
  maxCommitsToShow?: number; // limit commits shown in notification
}

/**
 * Custom hook for checking GitHub repository updates
 * Why this matters: Centralizes all update checking logic in a reusable hook
 * that can be used across components. Handles polling, error states, and
 * localStorage persistence automatically.
 */
export const useUpdateChecker = (options: UpdateCheckerOptions = {}) => {
  const {
    checkInterval = 5 * 60 * 1000, // 5 minutes default
    enabled = true,
    maxCommitsToShow = 5
  } = options;

  const [state, setState] = useState<UpdateCheckerState>({
    hasUpdate: false,
    latestCommits: [],
    isChecking: false,
    lastChecked: null,
    error: null,
    summary: 'No updates available'
  });

  // Use ref to store interval ID for cleanup
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Tracks the newest meaningful commit SHA seen for each repo on the last
  // check. Why this matters: when the user dismisses/reloads we need to mark
  // the current head of EACH repo as seen, not just one merged commit.
  const latestShaByRepoRef = useRef<Record<string, string>>({});

  // Get last known commit for a specific repo from localStorage
  const getLastKnownCommit = useCallback((repoKey: string): string | null => {
    try {
      const value = localStorage.getItem(`${LAST_KNOWN_COMMIT_PREFIX}-${repoKey}`);
      if (value) return value;
      // Fall back to the legacy single-repo key for the frontend repo only.
      if (repoKey === 'frontend') {
        return localStorage.getItem(LEGACY_LAST_KNOWN_COMMIT_KEY);
      }
      return null;
    } catch (error) {
      console.warn('Failed to read last known commit from localStorage:', error);
      return null;
    }
  }, []);

  // Save last known commit for a specific repo to localStorage
  const setLastKnownCommit = useCallback((repoKey: string, sha: string) => {
    try {
      localStorage.setItem(`${LAST_KNOWN_COMMIT_PREFIX}-${repoKey}`, sha);
    } catch (error) {
      console.warn('Failed to save last known commit to localStorage:', error);
    }
  }, []);

  /**
   * Check for updates from GitHub API
   * Why this matters: This is the core function that fetches new commits
   * and determines if there are meaningful updates to notify users about.
   */
  const checkForUpdates = useCallback(async (force = false) => {
    if (!enabled && !force) return;

    setState(prev => ({ ...prev, isChecking: true, error: null }));

    try {
      const repos = getConfiguredRepos();
      if (repos.length === 0) {
        throw new Error('No repositories configured. Please check REACT_APP_GITHUB_REPO / REACT_APP_GITHUB_BACKEND_REPO.');
      }

      // Check every configured repo in parallel and collect the new commits
      // for each. Why this matters: parallel fetches keep the check fast, and
      // computing "new" per repo (against that repo's own last-seen SHA) keeps
      // frontend and backend update detection independent of each other.
      const perRepoResults = await Promise.all(
        repos.map(async ({ key, repo }) => {
          const lastKnownCommit = getLastKnownCommit(key);
          const since = lastKnownCommit
            ? undefined
            : new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(); // last 24h if nothing known yet

          console.log('Checking for updates...', { repo, lastKnownCommit, since });

          const allCommits = await fetchLatestCommits(since, repo);
          const meaningfulCommits = filterMeaningfulCommits(allCommits);

          // Remember this repo's current head so dismiss/reload can mark it seen
          if (meaningfulCommits.length > 0) {
            latestShaByRepoRef.current[key] = meaningfulCommits[0].sha;
          }

          // Keep only commits newer than the one we've already seen for this repo
          const newCommits = lastKnownCommit
            ? meaningfulCommits.filter(commit => {
                const lastKnownIndex = meaningfulCommits.findIndex(c => c.sha === lastKnownCommit);
                const currentIndex = meaningfulCommits.findIndex(c => c.sha === commit.sha);
                return lastKnownIndex === -1 || currentIndex < lastKnownIndex;
              })
            : meaningfulCommits;

          return { key, repo, totalCommits: allCommits.length, meaningfulCommits, newCommits };
        })
      );

      // Merge new commits from all repos, newest first, so the single toast
      // reflects changes from either codebase.
      const mergedNewCommits = perRepoResults
        .flatMap(result => result.newCommits)
        .sort(
          (a, b) =>
            new Date(b.commit.author.date).getTime() - new Date(a.commit.author.date).getTime()
        );

      const hasNewUpdates = mergedNewCommits.length > 0;
      const commitsToShow = mergedNewCommits.slice(0, maxCommitsToShow);
      const summary = getCommitSummary(commitsToShow);

      setState(prev => ({
        ...prev,
        hasUpdate: hasNewUpdates,
        latestCommits: commitsToShow,
        isChecking: false,
        lastChecked: new Date(),
        error: null,
        summary
      }));

      console.log('Update check complete:', {
        repos: perRepoResults.map(r => ({
          repo: r.repo,
          total: r.totalCommits,
          meaningful: r.meaningfulCommits.length,
          new: r.newCommits.length
        })),
        hasUpdate: hasNewUpdates
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Failed to check for updates:', error);
      
      setState(prev => ({
        ...prev,
        isChecking: false,
        error: errorMessage,
        lastChecked: new Date()
      }));
    }
  }, [enabled, getLastKnownCommit, maxCommitsToShow]);

  /**
   * Dismiss the current update notification
   * Why this matters: When users click the notification or dismiss it,
   * we need to mark the current commits as "seen" so they don't get
   * notified about the same commits again.
   */
  const dismissUpdate = useCallback(() => {
    const seenByRepo = latestShaByRepoRef.current;
    const repoKeys = Object.keys(seenByRepo);

    if (repoKeys.length > 0) {
      // Mark the current head of every repo as seen so neither repo re-triggers
      repoKeys.forEach(repoKey => setLastKnownCommit(repoKey, seenByRepo[repoKey]));

      setState(prev => ({
        ...prev,
        hasUpdate: false,
        latestCommits: [],
        summary: 'No updates available'
      }));

      console.log('Update dismissed, marked commits as seen:', seenByRepo);
    }
  }, [setLastKnownCommit]);

  /**
   * Manually trigger an update check
   * Why this matters: Allows components to force a check when needed,
   * like when user clicks a "check now" button or when app gains focus.
   */
  const forceCheck = useCallback(() => {
    checkForUpdates(true);
  }, [checkForUpdates]);

  /**
   * Reset the update checker state
   * Why this matters: Useful for testing or when user wants to reset
   * their notification preferences.
   */
  const reset = useCallback(() => {
    try {
      // Clear per-repo keys plus the legacy single-repo key
      getConfiguredRepos().forEach(({ key }) =>
        localStorage.removeItem(`${LAST_KNOWN_COMMIT_PREFIX}-${key}`)
      );
      localStorage.removeItem(LEGACY_LAST_KNOWN_COMMIT_KEY);
      latestShaByRepoRef.current = {};
      setState({
        hasUpdate: false,
        latestCommits: [],
        isChecking: false,
        lastChecked: null,
        error: null,
        summary: 'No updates available'
      });
      console.log('Update checker reset');
    } catch (error) {
      console.warn('Failed to reset update checker:', error);
    }
  }, []);

  // Set up periodic checking
  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Initial check
    checkForUpdates();

    // Set up interval for periodic checks
    intervalRef.current = setInterval(() => {
      checkForUpdates();
    }, checkInterval);

    // Cleanup interval on unmount or dependency change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, checkInterval, checkForUpdates]);

  // Check for updates when page becomes visible (user returns to tab)
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, check for updates
        checkForUpdates();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, checkForUpdates]);

  return {
    // State
    hasUpdate: state.hasUpdate,
    latestCommits: state.latestCommits,
    isChecking: state.isChecking,
    lastChecked: state.lastChecked,
    error: state.error,
    summary: state.summary,
    
    // Actions
    checkForUpdates: forceCheck,
    dismissUpdate,
    reset,
    
    // Configuration
    enabled,
    checkInterval
  };
};
